// Fancaps API 客户端

export interface SearchRequest {
  query: string
  content_type: string
}

export interface SearchResult {
  title: string
  url: string
  content_type: string
  year?: string
}

export interface SearchResponse {
  query: string
  results: SearchResult[]
  total: number
  cached: boolean
}

export interface EpisodeItem {
  title: string
  url: string
  preview_images: string[]
  episode_number?: number
  image_count?: number
  count_method?: string
}

export interface EpisodesResponse {
  show_title: string
  episodes: EpisodeItem[]
  total: number
  cached: boolean
}

export interface ImageItem {
  id: string
  small_url: string
  large_url: string
  thumbnail_url: string
}

export interface ImagesResponse {
  episode_title: string
  images: ImageItem[]
  total: number
  cached: boolean
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:8000/api'

class APIError extends Error {
  constructor(public status: number, message: string, public detail?: any) {
    super(message)
    this.name = 'APIError'
  }
}

// 电影重定向错误类
export class MovieRedirectError extends APIError {
  constructor(public imageUrl: string, public movieTitle?: string) {
    super(400, '电影内容没有剧集列表，请直接查看图片')
    this.name = 'MovieRedirectError'
  }
}

import { api, downloadApi } from '../services/api'

// 使用统一的api.ts客户端，移除重复的认证和错误处理逻辑
export async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  try {
    const method = (options.method || 'GET').toLowerCase()
    const data = options.body ? JSON.parse(options.body as string) : undefined
    
    // 检测是否为下载相关的API请求，使用更长超时时间的客户端
    const isDownloadRequest = endpoint.includes('/download') || 
                             endpoint.includes('/images/') && method === 'post' && data?.image_ids
    
    let response
    switch (method) {
      case 'get':
        if (isDownloadRequest) {
          response = await downloadApi.get<T>(endpoint, { headers: options.headers })
        } else {
          response = await api.get<T>(endpoint, { headers: options.headers })
        }
        break
      case 'post':
        if (isDownloadRequest) {
          response = await downloadApi.post<T>(endpoint, data, { headers: options.headers })
        } else {
          response = await api.post<T>(endpoint, data, { headers: options.headers })
        }
        break
      case 'put':
        response = await api.put<T>(endpoint, data, { headers: options.headers })
        break
      case 'delete':
        response = await api.delete<T>(endpoint, { headers: options.headers })
        break
      case 'patch':
        response = await api.patch<T>(endpoint, data, { headers: options.headers })
        break
      default:
        throw new Error(`Unsupported HTTP method: ${method}`)
    }
    
    return response.data
  } catch (error: any) {
    // 检测电影重定向错误
    if (error.response?.status === 400 && 
        typeof error.response?.data?.detail === 'object' && 
        error.response?.data?.detail?.error === 'MOVIE_NO_EPISODES') {
      throw new MovieRedirectError(
        error.response.data.detail.image_url,
        error.response.data.detail.movie_title
      )
    }
    
    // 转换axios错误为APIError
    const status = error.response?.status || 0
    const message = error.response?.data?.detail || error.response?.data?.message || error.message || '请求失败'
    throw new APIError(status, message, error.response?.data)
  }
}

// 搜索API
export async function searchFancaps(params: SearchRequest): Promise<SearchResponse> {
  return await request<SearchResponse>('/search', {
    method: 'POST',
    body: JSON.stringify(params),
  })
}

// 获取搜索建议
export async function getSearchSuggestions(query: string): Promise<string[]> {
  const params = new URLSearchParams({ q: query })
  return await request<string[]>(`/search/suggestions?${params}`)
}

// 获取剧集列表
export async function getEpisodes(showUrl: string): Promise<EpisodesResponse> {
  return await request<EpisodesResponse>('/episodes/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      show_url: showUrl
    })
  })
}

// 获取图片列表
export async function getImages(episodeId: string): Promise<ImagesResponse> {
  return await request<ImagesResponse>(`/images/episode/${episodeId}`)
}

// 获取样本图片（基于实战经验的ID范围）
export async function getSampleImages(limit: number = 50): Promise<ImagesResponse> {
  return await request<ImagesResponse>(`/images/sample?limit=${limit}`)
}

// 智能获取剧集图片（分析真实ID范围）
export async function getSmartImages(episodeId: string, limit: number = 50): Promise<ImagesResponse> {
  return await request<ImagesResponse>(`/images/smart/${encodeURIComponent(episodeId)}?limit=${limit}`)
}

// Phase 5: 本地图片库 - 充分利用现有缓存
export async function getLocalImageLibrary(
  page: number = 1, 
  limit: number = 50, 
  sortBy: string = "date_desc"
): Promise<ImagesResponse> {
  return await request<ImagesResponse>(`/images/local-library?page=${page}&limit=${limit}&sort_by=${sortBy}`)
}

// 分类本地图片库
export interface CategoryInfo {
  show_name: string
  episode_count: number
  total_images: number
  episodes: EpisodeInfo[]
}

export interface EpisodeInfo {
  episode_name: string
  image_count: number
  images: ImageItem[]
}

export interface CategorizedLibraryResponse {
  categories: CategoryInfo[]
  total_images: number
  total_categories: number
}

export async function getCategorizedLocalLibrary(): Promise<CategorizedLibraryResponse> {
  return await request<CategorizedLibraryResponse>('/images/local-library-categorized')
}

export async function getCategoryEpisodeImages(
  categoryName: string, 
  episodeName: string, 
  page: number = 1, 
  limit: number = 50
): Promise<{
  category_name: string
  episode_name: string
  images: ImageItem[]
  total: number
  page: number
  limit: number
  cached: boolean
}> {
  const encodedCategory = encodeURIComponent(categoryName)
  const encodedEpisode = encodeURIComponent(episodeName)
  return await request(`/images/local-library/category/${encodedCategory}/episode/${encodedEpisode}?page=${page}&limit=${limit}`)
}

// 搜索历史分类系统
export interface SearchBasedCategoryInfo {
  show_name: string
  episode_count: number
  total_images: number
  episodes: SearchBasedEpisodeInfo[]
  last_viewed?: string // 添加最近浏览时间字段
}

export interface SearchBasedEpisodeInfo {
  episode_name: string
  classification_name: string
  image_count: number
  source: string
  created_at: string
  search_term: string
  episode_id: string
}

export interface SearchBasedLibraryResponse {
  categories: SearchBasedCategoryInfo[]
  total_images: number
  total_categories: number
}

export async function getSearchBasedLibrary(): Promise<SearchBasedLibraryResponse> {
  return await request<SearchBasedLibraryResponse>('/images/search-based-library')
}

export async function getSearchClassificationImages(
  classificationName: string, 
  page: number = 1, 
  limit: number = 500
): Promise<{
  classification_name: string
  images: ImageItem[]
  total: number
  page: number
  limit: number
  cached: boolean
}> {
  const encodedName = encodeURIComponent(classificationName)
  return request(`/api/images/search-based-library/classification/${encodedName}?page=${page}&limit=${limit}`)
}

export async function deleteEpisodeFromHistory(episodeId: string): Promise<{
  success: boolean
  message: string
  work_removed: boolean
  work_title?: string
  episode_title?: string
}> {
  const encodedId = encodeURIComponent(episodeId)
  return request(`/images/search-based-library/episode/${encodedId}`, {
    method: 'DELETE'
  })
}

export async function recordEpisodeAccess(
  searchTerm: string,
  showTitle: string,
  showId: string,
  episodeTitle: string,
  episodeId: string
): Promise<{success: boolean, message: string}> {
  const params = new URLSearchParams({
    search_term: searchTerm,
    show_title: showTitle,
    show_id: showId,
    episode_title: episodeTitle,
    episode_id: episodeId
  })
  
  return await request(`/images/record-episode-access?${params}`, {
    method: 'POST'
  })
}

export async function updateClassificationImages(
  classificationName: string,
  imageIds: string[]
): Promise<{success: boolean, message: string, image_count: number}> {
  const params = new URLSearchParams({
    classification_name: classificationName,
    ...Object.fromEntries(imageIds.map((id, index) => [`image_ids`, id]))
  })
  
  return await request(`/images/update-classification-images?${params}`, {
    method: 'POST'
  })
}

// 获取缓存统计信息
export async function getCacheStats(): Promise<any> {
  return await request<any>('/images/cache-stats')
}

// CDN切换功能
export async function getCdnStatus(): Promise<any> {
  return await request<any>('/images/cdn-status')
}

export async function switchCdn(useSmallCdn: boolean): Promise<any> {
  return await request<any>(`/images/cdn-switch?use_small_cdn=${useSmallCdn}`, {
    method: 'POST'
  })
}

// 下载图片
export async function downloadImages(imageIds: string[]): Promise<Blob> {
  try {
    const response = await api.post('/download/images', { image_ids: imageIds }, {
      responseType: 'blob'
    })
    
    return response.data
  } catch (error: any) {
    const status = error.response?.status || 0
    const message = error.response?.data?.detail || error.message || '下载失败'
    throw new APIError(status, message, error.response?.data)
  }
}

// 获取图片代理URL
export function getImageProxyUrl(imageId: string): string {
  return `${API_BASE_URL}/images/proxy/${imageId}`
}

// 健康检查
export async function healthCheck(): Promise<{ status: string; services: Record<string, string> }> {
  return await request<{ status: string; services: Record<string, string> }>('/health')
}

// ==================== 收藏功能相关接口 ====================

export interface WorkFavorite {
  id: string
  title: string
  type: string
  original_url: string
  created_at: string
}

export interface EpisodeFavorite {
  id: string
  title: string
  work_title: string
  work_id: string
  episode_url: string
  preview_images: string[]
  created_at: string
}

export interface ImageFavorite {
  id: string
  image_id: string
  work_title: string
  work_id: string
  episode_title: string
  episode_id: string
  small_url: string
  large_url: string
  created_at: string
}

export interface FavoritesData {
  works: WorkFavorite[]
  episodes: EpisodeFavorite[]
  images: ImageFavorite[]
}

export interface FavoritesResponse {
  success: boolean
  message: string
  data: FavoritesData
}

export interface FavoriteStatusResponse {
  success: boolean
  is_favorited: boolean
  item_type: string
  item_id?: string
  url?: string
}

export interface FavoritesStatsResponse {
  success: boolean
  message: string
  data: {
    total_works: number
    total_episodes: number
    total_images: number
    total_items: number
    latest_favorite_time?: string
  }
}

// 获取所有收藏数据
export async function getAllFavorites(): Promise<FavoritesResponse> {
  return await request<FavoritesResponse>('/favorites/')
}

// 添加作品收藏
export async function addWorkFavorite(title: string, workType: string, originalUrl: string): Promise<{
  success: boolean
  message: string
  data?: WorkFavorite
}> {
  return await request('/favorites/work', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title,
      work_type: workType,
      original_url: originalUrl
    })
  })
}

// 移除作品收藏
export async function removeWorkFavorite(workId: string): Promise<{
  success: boolean
  message: string
}> {
  return await request(`/favorites/work/${workId}`, {
    method: 'DELETE'
  })
}

// 添加集数收藏
export async function addEpisodeFavorite(
  title: string, 
  workTitle: string, 
  workId: string, 
  episodeUrl: string, 
  previewImages: string[] = []
): Promise<{
  success: boolean
  message: string
  data?: EpisodeFavorite
}> {
  return await request('/favorites/episode', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      title,
      work_title: workTitle,
      work_id: workId,
      episode_url: episodeUrl,
      preview_images: previewImages
    })
  })
}

// 移除集数收藏
export async function removeEpisodeFavorite(episodeId: string): Promise<{
  success: boolean
  message: string
}> {
  return await request(`/favorites/episode/${episodeId}`, {
    method: 'DELETE'
  })
}

// 添加图片收藏
export async function addImageFavorite(
  imageId: string,
  workTitle: string,
  workId: string,
  episodeTitle: string,
  episodeId: string,
  smallUrl: string,
  largeUrl: string
): Promise<{
  success: boolean
  message: string
  data?: ImageFavorite
}> {
  return await request('/favorites/image', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      image_id: imageId,
      work_title: workTitle,
      work_id: workId,
      episode_title: episodeTitle,
      episode_id: episodeId,
      small_url: smallUrl,
      large_url: largeUrl
    })
  })
}

// 移除图片收藏
export async function removeImageFavorite(imageId: string): Promise<{
  success: boolean
  message: string
}> {
  return await request(`/favorites/image/${imageId}`, {
    method: 'DELETE'
  })
}

// 检查收藏状态
export async function checkFavoriteStatus(
  itemType: 'work' | 'episode' | 'image',
  itemId?: string,
  url?: string
): Promise<FavoriteStatusResponse> {
  const params = new URLSearchParams({ item_type: itemType })
  if (itemId) params.append('item_id', itemId)
  if (url) params.append('url', url)
  
  return await request(`/favorites/status?${params.toString()}`)
}

// 获取收藏统计信息
export async function getFavoritesStats(): Promise<FavoritesStatsResponse> {
  return await request<FavoritesStatsResponse>('/favorites/stats')
}

// 导出收藏数据
export async function exportFavorites(): Promise<{
  success: boolean
  message: string
  data: any
}> {
  return await request('/favorites/export')
}

// 导入收藏数据
export async function importFavorites(favoritesData: any, mergeMode: boolean = true): Promise<{
  success: boolean
  message: string
  added_counts: {
    works: number
    episodes: number
    images: number
  }
}> {
  return await request('/favorites/import', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      favorites_data: favoritesData,
      merge_mode: mergeMode
    })
  })
}

// ==================== 缓存管理相关接口 ====================

// 清除图片列表缓存 - 修复版：支持精确范围控制
export async function clearImagesCache(options?: {
  episode_id?: string
  work_id?: string
  pattern?: string
  clear_all?: boolean
}): Promise<{
  success: boolean
  message: string
  cleared_items: number
  operation_type: string
}> {
  const params = new URLSearchParams()
  
  if (options?.episode_id) {
    params.append('episode_id', options.episode_id)
  } else if (options?.work_id) {
    params.append('work_id', options.work_id)
  } else if (options?.pattern) {
    params.append('pattern', options.pattern)
  } else if (options?.clear_all) {
    params.append('clear_all', 'true')
  }
  
  const queryString = params.toString()
  const url = `/images/cache${queryString ? `?${queryString}` : ''}`
  
  return await request(url, {
    method: 'DELETE'
  })
}

// 清除本地图片文件缓存 - 修复版：支持精确范围控制
export async function clearImageFilesCache(options?: {
  episode_id?: string
  clear_all?: boolean
}): Promise<{
  message: string
  deleted_files: number
  operation_type?: string
}> {
  const params = new URLSearchParams()
  
  if (options?.episode_id) {
    params.append('episode_id', options.episode_id)
  } else if (options?.clear_all) {
    params.append('clear_all', 'true')
  }
  
  const queryString = params.toString()
  const url = `/images/cache/files${queryString ? `?${queryString}` : ''}`
  
  return await request(url, {
    method: 'DELETE'
  })
}

// 清除搜索缓存
export async function clearSearchCache(pattern?: string): Promise<{
  message: string
  pattern: string
}> {
  const params = pattern ? `?pattern=${encodeURIComponent(pattern)}` : ''
  return await request(`/search/cache${params}`, {
    method: 'DELETE'
  })
}

// 批量删除收藏
export async function batchDeleteFavorites(
  workIds: string[] = [],
  episodeIds: string[] = [],
  imageIds: string[] = []
): Promise<{
  success: boolean
  message: string
  deleted_counts: {
    works: number
    episodes: number
    images: number
  }
  failed_items: string[]
}> {
  return await request('/favorites/batch/delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      work_ids: workIds,
      episode_ids: episodeIds,
      image_ids: imageIds
    })
  })
}